"use client";

import type { RefObject } from "react";
import {
  CopyButton,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { POST_TEST_PROBABILITY_CALCULATOR_STRINGS } from "@/components/tools/post-test-probability-calculator";
import { ChartDownloads } from "@/components/tools/stats-clinical/chart";
import {
  DataGrid,
  WorkingLine,
} from "@/components/tools/stats-clinical/data-grid";
import { FaganNomogram } from "@/components/tools/stats-clinical/fagan-nomogram";
import { t } from "@/lib/i18n/interpolate";
import {
  type ChainResult,
  perHundred,
} from "@/lib/tool-kit/stats-clinical/post-test";
import { type Formatter, fixed } from "@/lib/tool-kit/stats-research/format";

/*
 * The result panel of the post-test probability calculator. Strings are the widget's own.
 */

type Strings = typeof POST_TEST_PROBABILITY_CALCULATOR_STRINGS;

export interface StepLabel {
  /** What the reader entered for this test, already phrased ("Test 1: LR+ 4.5"). */
  text: string;
}

function ratio(fmt: Formatter, value: number): string {
  if (value === Number.POSITIVE_INFINITY) {
    return "∞";
  }
  return fmt(value, { maximumFractionDigits: value >= 100 ? 1 : 3 });
}

function odds(fmt: Formatter, value: number): string {
  return value === Number.POSITIVE_INFINITY
    ? "∞"
    : fmt(value, { maximumFractionDigits: 4 });
}

function percent(fmt: Formatter, probability: number, digits = 1): string {
  return `${fmt(probability * 100, { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
}

export function PostTestSummary({
  result,
  stepLabels,
  strings,
  fmt,
  svgRef,
}: {
  result: ChainResult;
  stepLabels: readonly string[];
  strings: Strings;
  fmt: Formatter;
  svgRef: RefObject<SVGSVGElement | null>;
}) {
  const { postTestProbability, preTestProbability, combinedLr } = result;
  const copyText = t(strings.report, {
    pre: percent(fmt, preTestProbability),
    count: fmt(result.steps.length),
    lr: ratio(fmt, combinedLr),
    post: percent(fmt, postTestProbability),
  });
  const rows = [
    [
      strings.stepStart,
      "—",
      odds(fmt, result.preTestOdds),
      percent(fmt, preTestProbability),
    ],
    ...result.steps.map((step, i) => [
      stepLabels[i],
      ratio(fmt, step.lr),
      odds(fmt, step.oddsAfter),
      percent(fmt, step.probabilityAfter),
    ]),
  ];
  return (
    <div aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.cardPost}
          value={percent(fmt, postTestProbability)}
        />
        <StatCard
          label={strings.cardOdds}
          value={odds(fmt, result.postTestOdds)}
        />
        <StatCard label={strings.cardLr} value={ratio(fmt, combinedLr)} />
      </StatGrid>
      <p className="text-foreground text-sm leading-relaxed">
        {t(strings.reading, {
          pre: percent(fmt, preTestProbability),
          post: percent(fmt, postTestProbability),
          perHundred: fmt(perHundred(postTestProbability)),
        })}
      </p>
      {result.steps.length > 1 ? (
        <p className="text-muted-foreground text-sm">
          {strings.independenceNote}
        </p>
      ) : null}
      <DataGrid
        head={[
          strings.columnStep,
          strings.columnLr,
          strings.columnOdds,
          strings.columnProbability,
        ]}
        rows={rows}
      />
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={copyText}
        />
        <span className="font-mono text-muted-foreground text-xs">
          {copyText}
        </span>
      </div>
      <section className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.nomogramHeading}</h3>
        <p className="text-muted-foreground text-sm">
          {result.steps.length > 1
            ? strings.nomogramNoteCombined
            : strings.nomogramNote}
        </p>
        <FaganNomogram
          description={t(strings.nomogramLabel, {
            pre: percent(fmt, preTestProbability),
            post: percent(fmt, postTestProbability),
          })}
          formatNumber={(value, digits = 1) =>
            fmt(value, { maximumFractionDigits: digits })
          }
          labels={{
            pre: strings.axisPre,
            ratio: strings.axisRatio,
            post: strings.axisPost,
          }}
          likelihoodRatio={combinedLr}
          postTestProbability={postTestProbability}
          preTestProbability={preTestProbability}
          svgRef={svgRef}
        />
        <ChartDownloads
          baseName="fagan-nomogram"
          pngLabel={strings.downloadPng}
          svgLabel={strings.downloadSvg}
          svgRef={svgRef}
        />
      </section>
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
        <WorkingLine>
          {t(strings.workingPre, {
            p: fixed(fmt, preTestProbability, 4),
            odds: odds(fmt, result.preTestOdds),
          })}
        </WorkingLine>
        <WorkingLine>
          {t(strings.workingPost, {
            pre: odds(fmt, result.preTestOdds),
            lr: ratio(fmt, combinedLr),
            odds: odds(fmt, result.postTestOdds),
          })}
        </WorkingLine>
        <WorkingLine>
          {t(strings.workingProbability, {
            odds: odds(fmt, result.postTestOdds),
            p: fixed(fmt, postTestProbability, 4),
          })}
        </WorkingLine>
      </div>
    </div>
  );
}
